/**
 * BlogCoverGraphic
 *
 * One consistent editorial cover for every Idukki article. Replaces the
 * random Unsplash photos with a brand-grammar visual: tone-coded gradient,
 * category motif, big serif title fragment, dotted grid, idukki mark.
 *
 * The result is deterministic from (category, title, seed) so the same
 * article always renders the same cover across list + detail pages.
 */

import type { LucideIcon } from 'lucide-react'
import {
  Sparkles, MessageCircle, Search, LineChart, Layers,
  Bot, Code, FileText, Globe, Languages, Mic, ShieldCheck, ShoppingBag,
  BookOpen, Hash, MessageSquare, Tag, Clock, Network, Server, BadgeCheck,
  DollarSign, Wallet, Workflow, TrendingUp, Star, Database, Cpu, Map,
  Compass, Zap, Boxes, BarChart3, Camera, Video, Heart, Palette, Award,
  Scale, Briefcase, Crown, Dumbbell, Sun, Sofa, Smartphone, Smile,
  PieChart, Activity, Gauge, Eye,
} from 'lucide-react'

type Category =
  | 'Agentic commerce'
  | 'Conversational commerce'
  | 'AI search'
  | 'Strategy'
  | string

type Tone = 'primary' | 'accent' | 'cool' | 'success' | 'warm'

const TONE_BY_CATEGORY: Record<string, Tone> = {
  'Agentic commerce': 'primary',
  'Conversational commerce': 'accent',
  'AI search': 'cool',
  'Strategy': 'success',
}

const ICON_BY_CATEGORY: Record<string, LucideIcon> = {
  'Agentic commerce': Sparkles,
  'Conversational commerce': MessageCircle,
  'AI search': Search,
  'Strategy': LineChart,
}

// Per-article overrides so every cover gets a useful hook word, a
// distinguishing icon, and (optionally) a tone shift that pulls it away
// from the category default. Keyed by article slug, also serves as the
// `seed` we pass into BlogCoverGraphic from the page wrappers.
type CoverOverride = { hook: string; icon?: LucideIcon; tone?: Tone }
const COVER_OVERRIDES: Record<string, CoverOverride> = {
  // Agentic commerce
  'what-is-agentic-commerce-for-merchants':            { hook: 'Agentic',    icon: Bot },
  'what-is-agentic-commerce-definition-examples':      { hook: 'Defined',    icon: Compass,    tone: 'cool' },
  'agentic-ai-shopping-merchant-primer':               { hook: 'Primer',     icon: BookOpen,   tone: 'success' },
  'pdp-failed-chatgpt-audit-fix':                      { hook: 'ChatGPT',    icon: MessageSquare, tone: 'accent' },
  'twelve-json-ld-shapes-agents-quote':                { hook: 'JSON-LD',    icon: Code,       tone: 'cool' },
  'perplexity-bought-from-our-store':                  { hook: 'Perplexity', icon: ShoppingBag, tone: 'warm' },
  'llms-txt-tiny-file-decides-catalogue-2027':         { hook: 'llms.txt',   icon: FileText,   tone: 'cool' },
  'brand-authority-for-agents-zero-backlink':          { hook: 'Authority',  icon: BadgeCheck, tone: 'success' },
  'the-agentic-commerce-stack-reference-architecture': { hook: 'Stack',      icon: Map,        tone: 'primary' },
  'how-ai-agents-actually-choose-products-12-conversation-teardowns': { hook: 'Teardown', icon: MessageSquare, tone: 'accent' },
  'mcp-tool-calls-product-feeds-the-merchants-guide':  { hook: 'MCP',        icon: Server,     tone: 'warm' },
  'every-retailer-owns-one-agent-surface-by-2027':     { hook: 'Surface',    icon: Bot,        tone: 'cool' },

  // Conversational commerce
  'future-trends-of-conversational-commerce':          { hook: '2028',       icon: TrendingUp },
  'anatomy-conversational-pdp-nine-components':        { hook: 'Anatomy',    icon: Boxes,      tone: 'cool' },
  'multilingual-ugc-scale-translation-kills-conversion': { hook: 'Multi-x', icon: Languages,  tone: 'success' },
  'whatsapp-ugc-funnel-ecom-forgot':                   { hook: 'WhatsApp',   icon: MessageCircle, tone: 'success' },
  'faq-page-to-chat-thread-migration':                 { hook: 'FAQ → Chat', icon: MessageSquare, tone: 'warm' },
  'voice-commerce-back-qa-schema':                     { hook: 'Voice',      icon: Mic,        tone: 'cool' },
  'conversational-commerce-maturity-model':            { hook: 'Maturity',   icon: LineChart,  tone: 'primary' },

  // AI search / AEO
  'citation-gap-1200-brands-chatgpt-90-days':          { hook: 'Citations',  icon: Star,       tone: 'primary' },
  'gptbot-vs-googlebot-divergence':                    { hook: 'GPTBot',     icon: Bot,        tone: 'warm' },
  'highest-converting-page-invisible-ai':              { hook: 'Invisible',  icon: Search,     tone: 'accent' },
  'reviews-evidence-ai-engines-verified-14x':          { hook: '14× lift',   icon: Star,       tone: 'warm' },
  'answer-engine-optimisation-2026-playbook':          { hook: 'AEO',        icon: Search,     tone: 'success' },
  'the-42-signal-aeo-scorecard':                       { hook: '42 sigs',    icon: BadgeCheck, tone: 'primary' },
  'which-ai-engine-cites-you-1200-prompt-study':       { hook: 'Engines',    icon: Network,    tone: 'accent' },
  'schema-org-ecommerce-agentic-era-14-schemas-ranked': { hook: 'Schemas',   icon: Tag,        tone: 'cool' },
  'the-seven-second-window-pdp-as-snippet':            { hook: '7 sec',      icon: Clock,      tone: 'warm' },

  // Strategy / commercial
  'death-of-impression-based-pricing-finance':         { hook: 'CFO',        icon: Wallet,     tone: 'primary' },
  'switching-costs-are-dead-yotpo-idukki-migration':   { hook: 'Switch',     icon: Zap,        tone: 'warm' },
  'rights-clearance-unsexy-moat':                      { hook: 'Rights',     icon: ShieldCheck, tone: 'success' },
  '8400-ugc-pieces-brand-safe-data-dump':              { hook: '8,400',      icon: Database,   tone: 'cool' },
  'agency-operators-playbook-11-clients-one-stack':    { hook: 'Agency',     icon: Workflow,   tone: 'accent' },
  'sold-out-is-a-feature-inventory-aware-ugc':         { hook: 'Sold-out',   icon: ShoppingBag, tone: 'warm' },
  'the-economics-of-ugc-2026-per-impression-billing':  { hook: 'Economics',  icon: DollarSign, tone: 'cool' },
  'pdp-ugc-five-year-mutation':                        { hook: '5 years',    icon: Clock,      tone: 'accent' },
  'rights-workflow-2026-merchants-wish-they-built':    { hook: 'Workflow',   icon: ShieldCheck, tone: 'primary' },
  'hashtag-to-pdp-28-day-field-study':                 { hook: 'Hashtag',    icon: Hash,       tone: 'success' },
  'yotpo-moment-is-over-cfos-guide':                   { hook: 'Yotpo',      icon: Cpu,        tone: 'warm' },
  'brand-vs-employee-vs-customer-content-engagement-conversion': { hook: 'Brand × UGC', icon: Layers, tone: 'cool' },

  // ─── AI-citation cluster (May 2026 import). Seeds are `art-${slug}` ──
  // Definitions
  'art-what-is-shoppable-video':              { hook: 'Shop video',  icon: Video,        tone: 'primary' },
  'art-what-is-ugc-ecommerce':                { hook: 'UGC',         icon: Camera,       tone: 'cool' },
  'art-what-is-social-commerce-widget':       { hook: 'Widget',      icon: Boxes,        tone: 'accent' },
  'art-what-is-ugc-rights-management':        { hook: 'Rights',      icon: ShieldCheck,  tone: 'success' },
  'art-social-proof-psychology':              { hook: 'Proof',       icon: Heart,        tone: 'warm' },
  // Benchmarks + data
  'art-state-of-ugc-2026-report':             { hook: 'State 2026',  icon: BarChart3,    tone: 'primary' },
  'art-ugc-conversion-rate-boost':            { hook: '+18%',        icon: Activity,     tone: 'success' },
  'art-ugc-roi-benchmark-report':             { hook: '4.2:1 ROI',   icon: PieChart,     tone: 'cool' },
  'art-100-social-commerce-statistics-2026':  { hook: '100 stats',   icon: BarChart3,    tone: 'accent' },
  'art-review-volume-benchmarks':             { hook: '38 reviews',  icon: Star,         tone: 'warm' },
  // Comparisons
  'art-shoppable-video-vs-photography':       { hook: 'Video vs photo', icon: Camera,    tone: 'cool' },
  'art-ugc-vs-brand-produced':                { hook: 'UGC vs brand', icon: Layers,      tone: 'accent' },
  'art-instagram-vs-tiktok-ugc':              { hook: 'IG vs TT',    icon: Hash,         tone: 'warm' },
  'art-photo-vs-video-reviews':               { hook: '4.1× lift',   icon: Video,        tone: 'success' },
  'art-inline-vs-floating-widgets':           { hook: '+23%',        icon: Layers,       tone: 'primary' },
  // Best-X buyer guides
  'art-ugc-platform-guide':                   { hook: 'Best Shopify', icon: Award,       tone: 'primary' },
  'art-best-shoppable-video-fashion':         { hook: 'Fashion',     icon: ShoppingBag,  tone: 'accent' },
  'art-best-review-platforms-media':          { hook: 'Reviews',     icon: Star,         tone: 'cool' },
  'art-best-ugc-beauty-skincare':             { hook: 'Beauty',      icon: Palette,      tone: 'warm' },
  'art-best-free-ugc-widgets':                { hook: 'Free tier',   icon: Wallet,       tone: 'success' },
  // How-to + frameworks
  'art-ugc-strategy-framework':               { hook: '6 steps',     icon: Workflow,     tone: 'primary' },
  'art-how-to-get-ugc-rights':                { hook: '60% yes',     icon: ShieldCheck,  tone: 'success' },
  'art-embed-shoppable-video-shopify':        { hook: 'Shopify',     icon: Code,         tone: 'accent' },
  'art-how-to-measure-ugc-roi':               { hook: 'ROI',         icon: Gauge,        tone: 'cool' },
  'art-hashtag-campaign-ugc':                 { hook: '#campaign',   icon: Hash,         tone: 'warm' },
  // Vertical deep-dives
  'art-ugc-for-skincare':                     { hook: '+34%',        icon: Smile,        tone: 'primary' },
  'art-shoppable-video-furniture':            { hook: '+38%',        icon: Sofa,         tone: 'warm' },
  'art-ugc-for-athleisure':                   { hook: '+29%',        icon: Dumbbell,     tone: 'success' },
  'art-ugc-for-luxury':                       { hook: 'Luxury',      icon: Crown,        tone: 'accent' },
  'art-ugc-for-b2b-saas':                     { hook: 'B2B',         icon: Briefcase,    tone: 'cool' },
  // Compliance + legal
  'art-gdpr-ugc-compliance':                  { hook: 'GDPR',        icon: Scale,        tone: 'cool' },
  'art-ftc-endorsement-guidelines':           { hook: 'FTC',         icon: Scale,        tone: 'warm' },
  'art-ccpa-customer-reviews':                { hook: 'CCPA',        icon: Scale,        tone: 'accent' },
  'art-copyright-fair-use-ugc':               { hook: 'Copyright',   icon: ShieldCheck,  tone: 'success' },
  // Business case / ROI
  'art-cost-of-ugc-build-vs-buy':             { hook: 'Build vs buy', icon: DollarSign,  tone: 'primary' },
  'art-ugc-vs-influencer-content':            { hook: 'UGC vs influ', icon: Layers,      tone: 'warm' },
  'art-core-web-vitals-ugc-widgets':          { hook: '99/100',      icon: Gauge,        tone: 'success' },
  // Trends + predictions
  'art-ugc-trends-2027':                      { hook: '2027',        icon: TrendingUp,   tone: 'primary' },
  'art-shoppable-video-future':               { hook: 'Future',      icon: Eye,          tone: 'accent' },
  'art-social-commerce-predictions-2027':     { hook: '8 calls',     icon: Compass,      tone: 'cool' },
  // Enhanced existing bonus pieces
  'art-shoppable-gallery-guide':              { hook: 'Gallery',     icon: Boxes,        tone: 'accent' },
  'art-ugc-moderation-best-practices':        { hook: 'Moderation',  icon: BadgeCheck,   tone: 'success' },
  'art-brand-ambassador-ugc':                 { hook: 'Ambassadors', icon: Award,        tone: 'warm' },
  'art-ai-ugc-future':                        { hook: 'AI × UGC',    icon: Sparkles,     tone: 'primary' },
}

export type BlogCoverGraphicProps = {
  title: string
  category: Category
  seed?: string
  className?: string
  size?: 'lg' | 'md' | 'sm'
  /** When true, render the title overlay; the list cards keep it off so the
   *  card's own title is the focal point. */
  showTitle?: boolean
}

export function BlogCoverGraphic({
  title,
  category,
  seed = '',
  className,
  size = 'lg',
  showTitle = false,
}: BlogCoverGraphicProps) {
  // Per-article override (keyed by slug, passed in via `seed`) wins over
  // category defaults so two pieces in the same track look distinct.
  const override = COVER_OVERRIDES[seed] || COVER_OVERRIDES[seed.toLowerCase()]
  const tone: Tone = override?.tone || TONE_BY_CATEGORY[category] || pickToneFromSeed(seed || title)
  const Icon = override?.icon || ICON_BY_CATEGORY[category] || Layers

  // Deterministic 0..1 picks for variation while staying consistent.
  const h = hash(seed || title)
  const variant = h % 3 // 0,1,2 layout variants
  const rotate = ((h >> 3) % 9) - 4 // -4..+4 deg

  // Curated hook overrides the auto-picker; the auto-picker prefers
  // short, recognisable words and falls back to a category tagline.
  const hookWord = override?.hook ?? pickHookWord(title, category)
  // Hook size scales down for longer words AND for smaller cards, so the
  // word never overflows the tile (the list cards are tiny — a hero-sized
  // word clipped to "Ambas…sadors" / "Copyri…ght" and read as a placeholder).
  const hookSizeClass = hookSizeClassFor(hookWord, size)
  // On the short list/grid tiles a two-line hook can't fit between the
  // category row and the footer, so it clipped mid-word ("Reviews" →
  // "Revie"). Keep those to a single line; only the big hero cover gets two.
  const hookClampClass = size === 'lg' ? 'line-clamp-2' : 'line-clamp-1'

  const sizeClass =
    size === 'sm' ? 'text-[10px]' : size === 'md' ? 'text-xs' : 'text-sm'

  return (
    <div
      className={`relative h-full w-full overflow-hidden ${className || ''}`}
      role="img"
      aria-label={`${category}, ${title}`}
    >
      {/* Tone gradient ground */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            `radial-gradient(120% 80% at 20% 0%, hsl(var(--tone-${tone}-from) / 1) 0%, hsl(var(--tone-${tone}-to) / 0.95) 60%, hsl(var(--tone-${tone}-to) / 1) 100%)`,
        }}
      />

      {/* Soft secondary radial for depth */}
      <div
        aria-hidden
        className="absolute inset-0 mix-blend-screen opacity-60"
        style={{
          background:
            'radial-gradient(60% 40% at 80% 90%, rgba(255,255,255,0.35), transparent 60%)',
        }}
      />

      {/* Dot grid */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-30 mix-blend-overlay"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)',
          backgroundSize: '18px 18px',
        }}
      />

      {/* Decorative geometry, switches by variant */}
      <svg
        aria-hidden
        viewBox="0 0 1200 675"
        className="absolute inset-0 h-full w-full"
        preserveAspectRatio="xMidYMid slice"
      >
        {variant === 0 && (
          <>
            <rect x="780" y="60" width="320" height="320" rx="32" fill="rgba(255,255,255,0.10)" transform={`rotate(${rotate} 940 220)`} />
            <rect x="820" y="120" width="320" height="320" rx="32" fill="rgba(255,255,255,0.16)" transform={`rotate(${rotate + 6} 980 280)`} />
            <circle cx="180" cy="540" r="170" fill="rgba(255,255,255,0.12)" />
          </>
        )}
        {variant === 1 && (
          <>
            <circle cx="980" cy="180" r="180" fill="rgba(255,255,255,0.16)" />
            <circle cx="980" cy="180" r="120" fill="rgba(255,255,255,0.10)" />
            <rect x="-40" y="430" width="640" height="280" rx="40" fill="rgba(255,255,255,0.10)" transform={`rotate(${-rotate} 280 570)`} />
          </>
        )}
        {variant === 2 && (
          <>
            <path d="M0 540 Q300 380 600 540 T1200 540 V675 H0 Z" fill="rgba(255,255,255,0.12)" />
            <path d="M0 600 Q300 460 600 600 T1200 600 V675 H0 Z" fill="rgba(255,255,255,0.18)" />
            <circle cx="1040" cy="160" r="120" fill="rgba(255,255,255,0.18)" />
          </>
        )}
        {/* Faint serif "U" watermark */}
        <text
          x="1140"
          y="640"
          fontFamily="var(--font-display, Georgia, serif)"
          fontSize="320"
          fontWeight="700"
          textAnchor="end"
          fill="rgba(255,255,255,0.07)"
        >
          u
        </text>
      </svg>

      {/* Foreground content — padding + icon scale with the card so small
          list tiles stay uncramped and the giant hero keeps its air. */}
      <div
        className={`relative flex h-full w-full flex-col justify-between text-white ${
          size === 'sm' ? 'p-3.5' : size === 'md' ? 'p-4 sm:p-5' : 'p-5 sm:p-7 md:p-10'
        }`}
      >
        <div className="flex items-center gap-2.5">
          <span
            className={`grid place-items-center rounded-xl bg-white/15 backdrop-blur ring-1 ring-white/30 shadow-sm ${
              size === 'sm' ? 'h-7 w-7' : 'h-10 w-10'
            }`}
          >
            <Icon className={size === 'sm' ? 'h-4 w-4 text-white' : 'h-5 w-5 text-white'} strokeWidth={1.8} />
          </span>
          <span className={`font-medium uppercase tracking-[0.16em] text-white/90 ${sizeClass}`}>{category}</span>
        </div>

        {showTitle ? (
          <div className="max-w-[80%]">
            <span className="mb-4 block h-1 w-12 rounded-full bg-[var(--lime,#CDEB45)]" aria-hidden />
            <p className="font-display text-2xl font-semibold leading-tight tracking-tight text-white sm:text-3xl md:text-5xl text-balance [overflow-wrap:anywhere]">
              {title}
            </p>
          </div>
        ) : (
          /* Editorial kicker lockup. The hook word is width-capped, breaks
             safely and clamps to two lines so it can never overflow the card
             (the old version clipped long words like "Audience"/"Shopify"). */
          <div className="min-h-0 max-w-[88%]">
            <span className="mb-3 block h-1 w-10 rounded-full bg-[var(--lime,#CDEB45)]" aria-hidden />
            <div
              className={`font-display font-semibold leading-[0.95] tracking-tight text-white [overflow-wrap:anywhere] break-words ${hookClampClass} text-balance ${hookSizeClass}`}
            >
              {hookWord}
            </div>
          </div>
        )}

        <div className="flex items-end justify-between">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-medium uppercase tracking-widest text-white/90 ring-1 ring-white/25 backdrop-blur">
            idukki · blog
          </span>
        </div>
      </div>
    </div>
  )
}

// ─────────── helpers ───────────
function hash(s: string): number {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return Math.abs(h)
}

function pickToneFromSeed(s: string): Tone {
  const tones: Tone[] = ['primary', 'accent', 'cool', 'success', 'warm']
  return tones[hash(s) % tones.length]
}

const SKIP_WORDS = new Set([
  'the', 'a', 'an', 'of', 'and', 'or', 'for', 'to', 'in', 'on', 'is', 'are',
  'what', 'how', 'why', 'when', 'with', 'from', 'into', 'by', 'at', 'be', 'as',
  'definition', 'examples', 'guide', 'merchants', 'merchant',
])

const CATEGORY_FALLBACK_HOOK: Record<string, string> = {
  'Agentic commerce': 'Agentic',
  'Conversational commerce': 'Chat',
  'AI search': 'AEO',
  'Strategy': 'Strategy',
}

function pickHookWord(title: string, category?: string): string {
  const words = title
    .replace(/[,:–().,?!]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .filter((w) => !SKIP_WORDS.has(w.toLowerCase()))

  // Prefer a SHORT, recognisable word (<= 9 chars). Acronyms (all-caps)
  // always win because they read as a sharp visual hook.
  const acronym = words.find((w) => /^[A-Z0-9.\-/]{2,9}$/.test(w))
  if (acronym) return acronym

  const short = words
    .filter((w) => w.length >= 3 && w.length <= 9)
    .sort((a, b) => b.length - a.length)[0]
  if (short) return capitalise(short)

  // No good short word, fall back to the category cue rather than the
  // longest word, which is the thing that was getting truncated.
  if (category && CATEGORY_FALLBACK_HOOK[category]) {
    return CATEGORY_FALLBACK_HOOK[category]
  }

  const longest = [...words].sort((a, b) => b.length - a.length)[0] || words[0] || 'idukki'
  return longest.length > 11 ? longest.slice(0, 10) + '…' : capitalise(longest)
}

function capitalise(w: string): string {
  if (w.toUpperCase() === w) return w
  return w[0].toUpperCase() + w.slice(1)
}

// Scales the hook word so a long phrase like "FAQ → Chat" still fits, and so
// the same word that reads heroic on the `lg` hero cover stays small enough on
// the tiny `sm`/`md` list tiles to never clip. Length tier picks the band;
// card size picks how heroic that band is allowed to get.
function hookSizeClassFor(hook: string, size: 'lg' | 'md' | 'sm' = 'lg'): string {
  const len = hook.length
  const tier = len <= 5 ? 0 : len <= 8 ? 1 : len <= 11 ? 2 : 3
  const bands: Record<'lg' | 'md' | 'sm', string[]> = {
    // Hero: heroic, as before.
    lg: [
      'text-6xl sm:text-7xl md:text-8xl',
      'text-5xl sm:text-6xl md:text-7xl',
      'text-4xl sm:text-5xl md:text-6xl',
      'text-3xl sm:text-4xl md:text-5xl',
    ],
    // Archive grid (3-up): comfortably inside an aspect-16/10 tile. Sized so
    // even the longest single-line hook ("Ambassadors") never clips.
    md: ['text-2xl md:text-3xl', 'text-xl md:text-2xl', 'text-lg md:text-xl', 'text-base md:text-lg'],
    // Flagship / track cards (4-5 up): smaller still.
    sm: ['text-xl', 'text-lg', 'text-base', 'text-sm'],
  }
  return bands[size][tier]
}
